import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Eraser, Info, Loader2, Save, Search } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarData } from '@/lib/utils';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

interface PoliticaRetencao {
  mesesAposConclusao: number;
  ativa: boolean;
}

interface ResumoRetencao {
  politica: PoliticaRetencao;
  elegiveis: number;
  comDadoPessoal: number;
  anexos: number;
  limite: string | null;
  maisAntiga: string | null;
}

export function PaginaRetencao() {
  const queryClient = useQueryClient();
  const [meses, setMeses] = React.useState('');
  const [confirmacao, setConfirmacao] = React.useState('');
  const [dialogoAberto, setDialogoAberto] = React.useState(false);

  const { data: politica, isPending } = useQuery({
    queryKey: ['retencao-politica'],
    queryFn: () => api.get<PoliticaRetencao>('/retencao/politica'),
  });

  React.useEffect(() => {
    if (politica) setMeses(String(politica.mesesAposConclusao));
  }, [politica]);

  const mesesNumero = Number(meses);
  const mesesValido = Number.isInteger(mesesNumero) && mesesNumero >= 0 && mesesNumero <= 600;

  const { data: simulacao, isFetching: simulando } = useQuery({
    queryKey: ['retencao-simulacao', mesesNumero],
    queryFn: () =>
      api.get<ResumoRetencao>(
        mesesNumero > 0 ? `/retencao/simulacao?meses=${mesesNumero}` : '/retencao/simulacao',
      ),
    enabled: mesesValido,
  });

  const invalidar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['retencao-politica'] });
    await queryClient.invalidateQueries({ queryKey: ['retencao-simulacao'] });
  };

  const salvar = useMutation({
    mutationFn: () => api.post('/retencao/politica', { mesesAposConclusao: mesesNumero }),
    onSuccess: async () => {
      toast.success(mesesNumero > 0 ? 'Prazo definido.' : 'Retenção desligada.');
      await invalidar();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.'),
  });

  const executar = useMutation({
    mutationFn: () =>
      api.post<{ anonimizadas: number; anexosRemovidos: number }>('/retencao/executar', {
        confirmacao: 'ANONIMIZAR',
      }),
    onSuccess: async (r) => {
      setDialogoAberto(false);
      setConfirmacao('');
      toast.success(
        `${r.anonimizadas} solicitação(ões) anonimizada(s) e ${r.anexosRemovidos} documento(s) removido(s).`,
      );
      await invalidar();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível executar.'),
  });

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-72 w-full max-w-2xl" />
      </div>
    );
  }

  const salvo = politica?.mesesAposConclusao ?? 0;
  const alterado = mesesValido && mesesNumero !== salvo;
  const haOQueAnonimizar = (simulacao?.comDadoPessoal ?? 0) > 0;

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Retenção de dados"
        descricao="Por quanto tempo os dados pessoais do paciente ficam no sistema depois que a solicitação se encerra."
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Anonimiza, não apaga.</p>
          <p className="mt-0.5">
            O protocolo, a especialidade, as datas e o desfecho continuam disponíveis para
            estatística e prestação de contas. Saem o nome, o CPF, o CNS, o telefone, o e-mail, o
            endereço, as observações e os documentos anexados. Do nascimento fica só o ano.
          </p>
        </div>
      </div>

      <Cartao className="mb-5">
        <CartaoCabecalho>
          <CartaoTitulo>Política</CartaoTitulo>
          <CartaoDescricao>
            {politica?.ativa ? (
              <span className="flex flex-wrap items-center gap-1.5">
                Em vigor: {politica.mesesAposConclusao} meses após a conclusão
                <Etiqueta tom="sucesso">ativa</Etiqueta>
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-1.5">
                Nenhum prazo definido — nada é anonimizado
                <Etiqueta>desligada</Etiqueta>
              </span>
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <div className="flex flex-col gap-4">
            <Campo
              id="retencao-meses"
              rotulo="Prazo em meses após a conclusão"
              ajuda="0 desliga a retenção. 60 meses equivalem a 5 anos."
              erro={mesesValido ? undefined : 'Informe um número inteiro entre 0 e 600.'}
            >
              <Entrada
                type="number"
                inputMode="numeric"
                className="w-40"
                value={meses}
                onChange={(evento) => setMeses(evento.target.value)}
              />
            </Campo>

            <div>
              <Botao
                type="button"
                onClick={() => salvar.mutate()}
                disabled={salvar.isPending || !alterado}
              >
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar prazo
              </Botao>
            </div>
          </div>
        </CartaoConteudo>
      </Cartao>

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Search className="size-4" aria-hidden="true" />O que seria anonimizado
          </CartaoTitulo>
          <CartaoDescricao>
            Simulação com o prazo digitado acima. Nada é alterado até você executar.
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          {simulando && <p className="text-silenciado-texto text-sm">Calculando…</p>}

          {!simulando && simulacao && (
            <div className="flex flex-col gap-3">
              {mesesNumero === 0 ? (
                <p className="text-silenciado-texto text-sm">
                  Com o prazo em 0, nada entra na anonimização.
                </p>
              ) : (
                <>
                  <dl className="grid gap-3 sm:grid-cols-3">
                    <Indicador rotulo="Concluídas no prazo" valor={simulacao.elegiveis} />
                    <Indicador
                      rotulo="Ainda com dado pessoal"
                      valor={simulacao.comDadoPessoal}
                      destaque
                    />
                    <Indicador rotulo="Documentos a remover" valor={simulacao.anexos} />
                  </dl>

                  {simulacao.limite && (
                    <p className="text-silenciado-texto text-sm">
                      Entram as solicitações concluídas antes de{' '}
                      <strong className="text-fundo-texto">{formatarData(simulacao.limite)}</strong>
                      {simulacao.maisAntiga && (
                        <> — a mais antiga é de {formatarData(simulacao.maisAntiga)}.</>
                      )}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </CartaoConteudo>
      </Cartao>

      {politica?.ativa && (
        <div className="border-status-devolvido-borda bg-status-devolvido-fundo mt-5 rounded-md border p-4">
          <p className="text-status-devolvido-texto flex items-start gap-2 text-sm font-medium">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Executar é irreversível
          </p>
          <p className="text-status-devolvido-texto mt-1 text-sm">
            Os dados anonimizados não voltam, nem por restauração de backup posterior à execução.
            Confira a simulação acima antes de prosseguir.
          </p>

          <Botao
            type="button"
            variante="destrutivo"
            className="mt-3"
            disabled={!haOQueAnonimizar}
            onClick={() => setDialogoAberto(true)}
          >
            <Eraser aria-hidden="true" />
            Executar anonimização
          </Botao>

          {!haOQueAnonimizar && (
            <p className="text-status-devolvido-texto mt-2 text-xs">
              Nada a anonimizar com o prazo em vigor.
            </p>
          )}
        </div>
      )}

      <Dialogo
        open={dialogoAberto}
        onOpenChange={(aberto) => {
          setDialogoAberto(aberto);
          if (!aberto) setConfirmacao('');
        }}
      >
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Anonimizar {simulacao?.comDadoPessoal} solicitação(ões)</DialogoTitulo>
            <DialogoDescricao>
              Serão removidos os dados pessoais e {simulacao?.anexos} documento(s) anexado(s). Não
              há como desfazer.
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoCorpo>
            <Campo id="retencao-confirmacao" rotulo="Digite ANONIMIZAR para confirmar" obrigatorio>
              <Entrada
                autoFocus
                autoComplete="off"
                value={confirmacao}
                onChange={(evento) => setConfirmacao(evento.target.value)}
              />
            </Campo>
          </DialogoCorpo>

          <DialogoRodape>
            <Botao type="button" variante="contorno" onClick={() => setDialogoAberto(false)}>
              Cancelar
            </Botao>
            <Botao
              type="button"
              variante="destrutivo"
              disabled={confirmacao !== 'ANONIMIZAR' || executar.isPending}
              onClick={() => executar.mutate()}
            >
              {executar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Anonimizar
            </Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>
    </div>
  );
}

function Indicador({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}) {
  return (
    <div className="border-borda rounded-md border p-3">
      <dt className="text-silenciado-texto text-xs">{rotulo}</dt>
      <dd
        className={`mt-0.5 text-2xl font-semibold tabular-nums ${
          destaque && valor > 0 ? 'text-alerta' : ''
        }`}
      >
        {valor}
      </dd>
    </div>
  );
}
